import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type MouseEvent } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { updateUser } from '../api';
import { type User } from '../types';
import { clientName, cn, formatShortDate, initialsOf } from '../utils';
import { CHIP_BUTTON_CLASS, CHIP_TONE_CLASS, verificationTone } from './verification-badge';
import { ConfirmationModal } from './confirmation-modal';
import { UserAvatar } from './user-avatar';

type Props = {
  user: User & { _id: string };
  bidCount: number;
  activeBidCount: number;
  /**
   * This row is the signed-in admin's own account. Their row still renders
   * (hiding it made a real client vanish from the list with no explanation —
   * see client-list.tsx) but the one mutation this screen offers — toggling
   * `verified` — is disabled here: an admin acting on their own row is the
   * scenario the original self-exclusion was guarding against, not a reason
   * to hide the row entirely.
   */
  isSelf?: boolean;
};

/**
 * One client — a table row from `lg` up, a card below it.
 *
 * Both viewports carry the same four facts the artboard names (Контакти,
 * Реєстрація, Верифікація, Ставки · ліміт); only the geometry changes, so the
 * desktop cells are emitted through `lg:contents` instead of a second copy of
 * the data. The verification pill stays a button: toggling verification is the
 * one mutation this screen has always had, and it survives the redraw.
 */
export function ClientListItem({ user, bidCount, activeBidCount, isSelf = false }: Props) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const name = clientName(user);
  // `clientName` falls back to the email when there is no real name on file —
  // in that case the email is already the bold identity line, so the
  // secondary "email" line below would just repeat it.
  const nameIsEmail = Boolean(user.email) && name === user.email;
  const hasActiveBids = activeBidCount > 0;
  const limit = user.bidLimit ?? 1;
  const { tone, label } = verificationTone(user);

  const mutation = useMutation({
    mutationFn: () => updateUser(user._id, { verified: !user.verified }),
    onSuccess: () => {
      toast.success(t('messages.verificationStatusUpdated'));
      setIsModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: () => toast.error(t('clients.errors.failedToUpdateUser')),
  });

  const openModal = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsModalOpen(true);
  };

  const statusLabel = mutation.isPending ? t('common.loading.default') : label;
  const bidsText = hasActiveBids
    ? t('clients.list.bidsCell', { total: bidCount, active: activeBidCount })
    : String(bidCount);
  // The card repeats the same count in the artboard's shorter phrasing, then
  // the limit — one line under the email instead of two table cells.
  const bidsTextShort = hasActiveBids
    ? t('clients.list.bidsCellShort', { total: bidCount, active: activeBidCount })
    : String(bidCount);
  const bidsTone = hasActiveBids ? 'text-cf-ok' : 'text-cf-ink-subtle';

  const verificationButton = isSelf ? (
    <span
      title={t('clients.list.you')}
      className={cn(CHIP_BUTTON_CLASS, CHIP_TONE_CLASS[tone], 'cursor-default opacity-60')}
    >
      {t('clients.list.you')}
    </span>
  ) : (
    <button
      type="button"
      onClick={openModal}
      disabled={mutation.isPending}
      className={cn(CHIP_BUTTON_CLASS, CHIP_TONE_CLASS[tone], 'disabled:opacity-50')}
    >
      {statusLabel}
    </button>
  );

  return (
    <>
      <Link
        to="/clients/$id"
        params={{ id: user._id }}
        className="flex items-center gap-3 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 text-left lg:grid lg:min-w-[880px] lg:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_110px_130px_150px_40px] lg:items-center lg:gap-4 lg:rounded-none lg:border-0 lg:border-b lg:border-cf-line-soft lg:px-[18px] lg:py-3 lg:hover:bg-cf-surface-sunken"
      >
        {/* ── Mobile card ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 items-center gap-3 lg:hidden">
          <UserAvatar
            photoURL={user.photoURL}
            initials={initialsOf(name)}
            tone={hasActiveBids ? 'brand' : 'fill'}
            className="h-10 w-10 text-[13px]"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] leading-[19px] font-semibold">{name}</div>
            {!nameIsEmail && (
              <div className="truncate text-xs leading-[15px] text-cf-ink-subtle">{user.email}</div>
            )}
            <div className="mt-[5px] flex flex-wrap items-center gap-2">
              {verificationButton}
              <span className={cn('font-mono text-[11px] leading-[14px]', bidsTone)}>
                {bidsTextShort} · {t('clients.list.limitCell', { limit })}
              </span>
            </div>
          </div>
        </div>

        {/* ── Desktop cells ───────────────────────────────────────────── */}
        <div className="hidden lg:contents">
          <div className="flex min-w-0 items-center gap-3">
            <UserAvatar
              photoURL={user.photoURL}
              initials={initialsOf(name)}
              tone={hasActiveBids ? 'brand' : 'fill'}
              className="h-9 w-9 text-[13px]"
            />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{name}</div>
              <div className="truncate text-xs text-cf-ink-subtle">{user.displayName || '—'}</div>
            </div>
          </div>

          <div className="min-w-0 text-[13px]">
            {!nameIsEmail && <div className="truncate leading-[17px]">{user.email}</div>}
            <div className="truncate font-mono text-xs leading-[15px] text-cf-ink-subtle">
              {user.phone || '—'}
            </div>
          </div>

          <span className="font-mono text-xs leading-[15px] text-cf-ink-muted">
            {formatShortDate(user.createdAt)}
          </span>

          {verificationButton}

          <div className="flex flex-col items-end text-right">
            <span className={cn('font-mono text-[13px] leading-[17px]', bidsTone)}>{bidsText}</span>
            {/* A caption, not an editor: the limit is part of the client's
                bidding mode now, and changing it outside «Режим ставок» would
                write `users.bidLimit` behind the mode's back (no journal row). */}
            <span className="text-xs leading-[15px] text-cf-ink-subtle">
              {t('clients.list.limitCell', { limit })}
            </span>
          </div>
        </div>
      </Link>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => mutation.mutate()}
        title={t('clients.confirmVerificationChange')}
        message={`${t('clients.confirmVerificationMessage')} ${name}`}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        isLoading={mutation.isPending}
      />
    </>
  );
}
